import * as React from 'react';

import { getSortCompareFn, type SortOptions, type SortBy } from './util';

export type { SortBy, SortOptions };
export type TableHeaderClassName = (sortByValue: SortBy['value'] | null, defaultClassName?: string) => string;

export type TableHeader = {
  className?: TableHeaderClassName;
  keyPath?: string;
  value: React.ReactNode;
};

export interface TableProps<TDataItem> {
  className?: string;
  tableHeaders: {
    defaultClassName?: (sortByValue: SortBy['value'] | null) => string;
    columns: TableHeader[];
  };
  data: TDataItem[];

  children:
    | React.ReactNode
    | ((props: {
        row: {
          data: TDataItem;
          index: number;
        };
      }) => React.ReactNode);

  /* allows customising what is rendered when there are no rows in the table, defaults to nothing being rendered */
  emptyStatePlaceholder?: React.ReactNode;

  /* allows overriding default behavior, which just uses the sortBy value/keyPath */
  sortOptions?: (sortBy: SortBy) => SortOptions;
  /* callback when sort order changes */
  onSortByChange?: (sortBy: SortBy) => void;

  /* specify the sort, if set, the table works like a controlled component, i.e does not change sort order by itself */
  sortBy?: SortBy;
  /* default sort by, should be set if sortBy is undefined (i.e uncontrolled component) */
  defaultSortBy?: SortBy;
}

export interface SortedDataItem<TDataItem> {
  data: TDataItem;
  index: number;
}

export interface TableState<TDataItem> {
  sortBy?: SortBy;
  sortedData: SortedDataItem<TDataItem>[];
  isControlled: boolean;
}

function getSortedData<TDataItem>(data: TDataItem[], sortOptions?: SortOptions): SortedDataItem<TDataItem>[] {
  if (!sortOptions) {
    return data.map((data, index) => ({ data, index }));
  }

  const compareFn = getSortCompareFn(sortOptions);
  return data.map((data, index) => ({ data, index })).sort(({ data: a }, { data: b }) => compareFn(a, b));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSortOptions(props: TableProps<any>, sortBy: SortBy) {
  if (!props.sortOptions) {
    return { keyPath: sortBy.keyPath, value: sortBy.value };
  }

  return props.sortOptions(sortBy);
}

function getUpdatedSortBy(key: string, previousSortBy?: SortBy): SortBy {
  return {
    keyPath: key,
    value: !previousSortBy || !previousSortBy.value || previousSortBy.value === 'ASC' ? 'DESC' : 'ASC',
  };
}

export class Table<TDataItem> extends React.PureComponent<TableProps<TDataItem>, TableState<TDataItem>> {
  constructor(props: TableProps<TDataItem>) {
    super(props);
    this.state = { isControlled: props.sortBy !== undefined, sortBy: undefined, sortedData: [] };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static getDerivedStateFromProps(nextProps: TableProps<any>, previousState: TableState<any>) {
    if (previousState.isControlled) {
      return { sortBy: nextProps.sortBy, sortedData: nextProps.data };
    }

    const sortBy = previousState.sortBy || nextProps.defaultSortBy;
    return { sortBy, sortedData: getSortedData(nextProps.data, sortBy && getSortOptions(nextProps, sortBy)) };
  }

  handleToggleSort = (keyPath: string | undefined, isControlled: boolean) => {
    if (!keyPath) {
      return;
    }

    const { data, onSortByChange } = this.props;

    if (!isControlled) {
      // Act like an uncontrolled component, sort the data and then send callback
      return this.setState(
        (prevState) => {
          const sortBy = getUpdatedSortBy(keyPath, prevState.sortBy);
          return { sortBy, sortedData: getSortedData(data, getSortOptions(this.props, sortBy)) };
        },
        () => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          onSortByChange && this.state.sortBy && onSortByChange(this.state.sortBy);
        },
      );
    }

    // Act like a controlled component, fire the callback and that is it
    const sortBy = getUpdatedSortBy(keyPath, this.state.sortBy);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onSortByChange && onSortByChange(sortBy);
  };

  getHeaderClassName = (header: TableHeader, sortBy?: SortBy) => {
    const { tableHeaders } = this.props;
    const sortValue = sortBy && sortBy.keyPath === header.keyPath ? sortBy.value : null;
    const defaultClassName =
      tableHeaders.defaultClassName !== undefined ? tableHeaders.defaultClassName(sortValue) : undefined;

    if (header.className) {
      return header.className(sortValue, defaultClassName);
    }

    return defaultClassName;
  };

  render() {
    const { className, tableHeaders, data, children, emptyStatePlaceholder } = this.props;
    const { isControlled, sortedData } = this.state;

    const sortBy = isControlled ? this.props.sortBy : this.state.sortBy;
    const isEmpty = isControlled ? data.length === 0 : sortedData.length === 0;
    const renderedChildren =
      typeof children === 'function'
        ? isControlled
          ? data.map((data, index) => children({ row: { data, index } }))
          : sortedData.map(({ data, index }) => children({ row: { data, index } }))
        : children;

    return (
      <table className={className}>
        <thead>
          <tr>
            {tableHeaders.columns.map((header, idx) => {
              return (
                <th
                  key={idx}
                  className={this.getHeaderClassName(header, sortBy)}
                  onClick={() => this.handleToggleSort(header.keyPath, isControlled)}>
                  {header.value}
                </th>
              );
            })}
          </tr>
        </thead>
        {isEmpty ? emptyStatePlaceholder : <tbody>{renderedChildren}</tbody>}
      </table>
    );
  }
}
